import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { GetBotResponse } from 'src/app/models/bot.interface';
import { MessengerBot } from 'src/app/models/messenger-bot.interface';
import { AttentionScheduleConfig, UpdateAttentionSchedulePayload } from 'src/app/models/attention-schedule.interface';
import { MessengerBotService } from 'src/app/core/services/messenger-bot.service';
import { DataService } from '../../data-service.service';
import { environment } from 'src/environments/environment';
declare const FB: any;

interface MessengerBenefitItem {
  icon: string;
  title: string;
  subtitle: string;
}

@Component({
  selector: 'globot-messenger-bot',
  templateUrl: './messenger-bot.component.html',
  styleUrls: ['./messenger-bot.component.scss'],
})
export class MessengerBotComponent implements OnInit, OnDestroy {
  @Input() bot?: GetBotResponse;
  pageSubscription: MessengerBot[] = [];

  /**
   * GLOB-1868 — Guided empty state (AC1): shown while no Facebook page is
   * linked yet. Reactive to `pageSubscription` (set from live backend state
   * in setStatus()/getMessengerBot()), so it disappears as soon as a page is
   * connected — no one-time flag.
   */
  get isConnected(): boolean {
    return this.pageSubscription.length > 0;
  }

  readonly benefitItems: MessengerBenefitItem[] = [
    {
      icon: 'pi pi-comments',
      title: 'Respuestas automáticas 24/7',
      subtitle: 'Tu asistente virtual atiende los mensajes de Messenger en cualquier momento',
    },
    {
      icon: 'pi pi-inbox',
      title: 'Gestión centralizada',
      subtitle: 'Todas tus conversaciones de Messenger en un solo dashboard',
    },
    {
      icon: 'pi pi-sliders-h',
      title: 'Actívalo cuando quieras',
      subtitle: 'Enciende o pausa tu asistente virtual en este canal sin perder la conexión',
    },
  ];
  private refresh!: Subscription;
  botEnabled: boolean = true;
  loading: boolean = false;
  deleteDialogVisible: boolean = false;
  facebookApiVersion = environment.facebookApiVersion;
  facebookAppId = environment.facebookAppId;
  messengerConfigId = environment.messengerConfigId;
  instagramConfigId = environment.instagramConfigId;
  facebookAppSecret = environment.facebookAppSecret;
  showIgAccountDetected = false;
  attentionScheduleConfig: AttentionScheduleConfig | null = null;
  savingAttentionSchedule = false;
  constructor(
    private fb: FormBuilder,
    private messengerBotService: MessengerBotService,
    private ms: MessageService,
    private dataService: DataService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  showMessage(severity: string, summary: string, detail: string) {
    this.ms.add({ key: 'tst', severity: severity, summary: summary, detail: detail });
  }

  async linkFacebookAccount() {
    this.loading = true;
    const response = await this.facebookLogin();
    if (!response.authResponse) {
      this.loading = false;
      return;
    }
    let userAccessToken;
    const access_token = await this.generateAccessToken(response.authResponse.code);
    if (access_token) {
      const accessTokenUser = access_token;
      if (!accessTokenUser) {
        this.ms.add({
          key: 'tst',
          severity: 'error',
          summary: 'Acción fallida',
          detail: 'No se pudo establecer el inicio de sesión con Facebook.',
        });
        this.loading = false;
        return;
      }
      userAccessToken = accessTokenUser;

      const pages = await this.getPages(userAccessToken);
      if (!pages) {
        this.ms.add({
          key: 'tst',
          severity: 'error',
          summary: 'Acción fallida',
          detail: 'No se pudo  obtener la página permitida por el usuario.',
        });
        this.loading = false;
        return;
      }
      const userData = await this.getUserData(userAccessToken);
      const userId = userData.id;
      if (pages.length != 1) {
        this.ms.add({
          key: 'tst',
          severity: 'error',
          summary: 'Acción fallida',
          detail: 'Porfavor, selecciona solo una  página para vincular.',
        });
        this.changeDetectorRef.detectChanges();
        //! REVOCAR PERMISOS
        await this.revokePermissions(userId, userAccessToken);
        this.facebookLogout();
        this.loading = false;
        return;
      }
      //* GUARDAR ID_PAGE, NOMBRE DE PAGINA asociadas al bot
      for await (const page of pages) {
        const success = await this.subscribePage(page.id, page.access_token);
        if (success) {
          const categories = page.category_list.map((c: any) => c.name) || [];
          this.messengerBotService
            .postPageSubscription(
              this.bot!.id,
              page.id,
              true,
              page.name,
              categories,
              page.access_token
            )
            .subscribe({
              next: (_response) => {
                this.getMessengerBot();
                this.loading = false;
                this.showMessage('success', 'Acción exitosa', page.name + ' vinculada con éxito!');
              },
              error: (_err) => {
                this.loading = false;
              },
            });
        } else {
          this.showMessage(
            'error',
            'Acción fallida',
            'No se pudo suscribir la página ' + page.name + ' porfavor  vuelva a intentarlo.'
          );
        }
        this.loading = false;
      }
    }
  }

  async facebookLogin(configuration?: number): Promise<any> {
    let configuration_id = configuration ?? this.messengerConfigId;
    let type_resp = configuration != null ? 'token' : 'code';
    return new Promise((resolve, reject) => {
      FB.login(
        async (response: any) => {
          if (response) {
            resolve(response);
          } else {
            reject(new Error('Facebook login failed'));
          }
        },
        {
          config_id: configuration_id, //781973270459738 id
          // config_id: 379742928129101,
          response_type: type_resp,
          override_default_response_type: true,
        }
      );
    });
  }

  async unlinkFacebookAccount() {
    this.showDeleteDialog(false);
    this.loading = true;
    if (this.pageSubscription.length < 1) {
      this.showMessage('error', 'Acción fallida', 'No se pudo desvincular la página');
      this.loading = false;
      return;
    }
    const page = this.pageSubscription[0];
    const unSuscribed = await this.unSubscribePage(page.page_id, page.page_access_token);
    if (!unSuscribed) {
      this.showMessage('error', 'Acción fallida', 'No se pudo desvincular la página');
      this.loading = false;
      return;
    }
    this.messengerBotService.deleteBotMessenger(this.bot!.id).subscribe({
      next: (_response) => {
        this.getMessengerBot();
        this.ms.add({
          key: 'tst',
          severity: 'success',
          summary: 'Acción exitosa',
          detail: 'Messenger Bot desvinculado!',
        });
        this.loading = false;
      },
      error: (_err) => {
        this.loading = false;
      },
    });
  }

  facebookLogout() {
    FB.getLoginStatus(function (response: any) {
      if (response.status === 'connected') {
        FB.logout(function (_response: any) {});
      }
    });
  }

  async subscribePage(pageId: string, pageAccessToken: string): Promise<boolean | undefined> {
    return new Promise((resolve, reject) => {
      FB.api(
        `/${pageId}/subscribed_apps`,
        'POST',
        {
          subscribed_fields: 'messages',
          access_token: pageAccessToken,
        },
        function (response: any) {
          if (response.success) {
            resolve(response.success);
          } else {
            reject(new Error('Failed to subscribe page'));
          }
        }
      );
    });
  }

  async unSubscribePage(pageId: string, pageAccessToken: string): Promise<boolean | undefined> {
    return new Promise((resolve, reject) => {
      FB.api(
        `/${pageId}/subscribed_apps`,
        'DELETE',
        {
          subscribed_fields: 'messages',
          access_token: pageAccessToken,
        },
        function (response: any) {
          if (response.success) {
            resolve(response.success);
          } else {
            reject(new Error('Failed to unsubscribe page'));
          }
        }
      );
    });
  }

  async getLoginStatus(): Promise<unknown> {
    return new Promise((resolve, _reject) => {
      FB.getLoginStatus((authResponse: any) => {
        if (authResponse.authResponse !== null) {
          resolve(authResponse);
        } else {
          resolve(false);
        }
      });
    });
  }

  async getPages(accessToken: string): Promise<[any] | undefined> {
    return new Promise((resolve, reject) => {
      FB.api(
        '/me/accounts',
        'GET',
        { access_token: accessToken },
        function (accountsResponse: any) {
          if (accountsResponse && accountsResponse.data.length > 0) {
            resolve(accountsResponse.data);
          } else {
            reject(new Error('No accounts found'));
          }
        }
      );
    });
  }

  async getUserData(accessToken: string): Promise<any> {
    return new Promise((resolve, reject) => {
      FB.api('/me', 'GET', { access_token: accessToken }, function (userDataResponse: any) {
        if (userDataResponse) {
          resolve(userDataResponse);
        } else {
          reject(new Error('Failed to get user data'));
        }
      });
    });
  }

  async revokePermissions(userId: string, accessToken: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      FB.api(
        `/${userId}/permissions`,
        'DELETE',
        { access_token: accessToken },
        function (revokeResponse: any) {
          if (revokeResponse) {
            resolve(revokeResponse);
          } else {
            reject(new Error('Failed to revoke permissions'));
          }
        }
      );
    });
  }

  async generateAccessToken(code: string): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
      FB.api(
        '/oauth/access_token',
        'GET',
        {
          client_id: environment.facebookAppId,
          client_secret: environment.facebookAppSecret,
          code: code,
        },
        function (authResponse: any) {
          if (authResponse.access_token) {
            resolve(authResponse.access_token);
          } else {
            reject(new Error('Failed to generate access token'));
          }
        }
      );
    });
  }

  switchChanged(event: any) {
    if (!this.bot?.id) {
      return;
    }

    const previousValue = this.botEnabled;
    this.botEnabled = event.checked; // Actualizar inmediatamente

    this.messengerBotService.updatePageSubscription(this.bot.id, event.checked).subscribe({
      next: (_response) => {
        this.getMessengerBot();
        this.ms.add({
          key: 'tst',
          severity: 'success',
          summary: 'Acción exitosa',
          detail: `Messenger Bot ${event.checked ? 'habilitado' : 'deshabilitado'}!`,
        });
      },
      error: (_err) => {
        this.botEnabled = previousValue; // Revertir en caso de error
        this.ms.add({
          key: 'tst',
          severity: 'error',
          summary: 'Acción fallida',
          detail: 'Hubo un problema al actualizar el estado del bot de Messenger.',
        });
      },
    });
  }

  ngOnInit(): void {
    FB.init(
      {
        appId: this.facebookAppId,
        cookie: true,
        xfbml: true,
        version: this.facebookApiVersion, // Use this Graph API version for this call.
      },
      {
        config_id: this.instagramConfigId,
      }
    );
    this.getMessengerBot();
    this.refresh = this.dataService.refreshMessenger().subscribe((value) => {
      if (value) {
        this.getMessengerBot();
      }
    });
  }

  ngOnDestroy() {
    this.refresh.unsubscribe();
  }

  async getMessengerBot() {
    this.messengerBotService.getBotMessenger(this.bot!.id).subscribe({
      next: (response) => {
        this.setStatus(response.status!, response.data);
      },
      error: (_err) => {
        this.setStatus('NOT_FOUND', []);
      },
    });
  }

  setStatus(status: string, data: MessengerBot[]) {
    switch (status) {
      case 'ENABLED':
        this.dataService.setMessengerStatus({
          status: 'Activo',
          style: {
            'padding-left': '10px',
            'padding-right': '10px',
            background: '#BBF7D0',
            color: '#16A34A',
          },
          messengerBot: data[0],
        });
        this.botEnabled = true;
        this.changeDetectorRef.detectChanges();
        this.pageSubscription = data;
        this.attentionScheduleConfig = this.toAttentionScheduleConfig(data[0]);
        this.changeDetectorRef.detectChanges();
        break;
      case 'DISABLED':
        this.dataService.setMessengerStatus({
          status: 'Inactivo',
          style: {
            'padding-left': '10px',
            'padding-right': '10px',
            background: '#FFF9CE',
            color: '#E0A53E',
          },
          messengerBot: data[0],
        });
        this.botEnabled = false;
        this.pageSubscription = data;
        this.attentionScheduleConfig = this.toAttentionScheduleConfig(data[0]);
        this.changeDetectorRef.detectChanges();
        break;
      default:
        this.dataService.setMessengerStatus({
          status: 'Sin configurar',
          style: {
            'padding-left': '10px',
            'padding-right': '10px',
            background: '#EDEDED',
            color: '#6B6B6B',
          },
          messengerBot: undefined,
        });
        this.botEnabled = false;
        this.pageSubscription = [];
        this.attentionScheduleConfig = null;
        this.changeDetectorRef.detectChanges();
        break;
    }
  }

  private toAttentionScheduleConfig(bot: MessengerBot | undefined): AttentionScheduleConfig | null {
    if (!bot) {
      return null;
    }
    return {
      attention_schedule_enabled: !!bot.attention_schedule_enabled,
      attention_schedule_start_time: bot.attention_schedule_start_time ?? null,
      attention_schedule_end_time: bot.attention_schedule_end_time ?? null,
      away_message_enabled: !!bot.away_message_enabled,
      away_message_text: bot.away_message_text ?? null,
    };
  }

  saveAttentionSchedule(payload: UpdateAttentionSchedulePayload): void {
    if (!this.bot?.id) {
      return;
    }
    const previousConfig = this.attentionScheduleConfig;
    this.savingAttentionSchedule = true;
    this.messengerBotService.updateAttentionSchedule(this.bot.id, payload).subscribe({
      next: () => {
        this.attentionScheduleConfig = { ...this.attentionScheduleConfig, ...payload } as AttentionScheduleConfig;
        this.savingAttentionSchedule = false;
        this.showMessage('success', 'Acción exitosa', 'Horario de atención actualizado');
      },
      error: (err) => {
        this.savingAttentionSchedule = false;
        this.attentionScheduleConfig = { ...previousConfig } as AttentionScheduleConfig;
        const detail = err?.error?.message || 'No se pudo guardar el horario de atención.';
        this.showMessage('error', 'Error al guardar', detail);
      },
    });
  }

  showDeleteDialog(value: boolean) {
    this.deleteDialogVisible = value;
  }

  closeDialogDelete(_value: boolean) {
    this.showDeleteDialog(false);
    this.loading = false;
  }

  deleteAccountSubscription() {
    this.unlinkFacebookAccount();
  }
}
